import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

export const VendorPortalLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle click outside dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  useEffect(() => {
    // Load theme preference
    const savedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-theme", savedTheme);
    document.body.setAttribute("data-theme", savedTheme);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      navigate("/login", { replace: true });
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    document.body.setAttribute("data-theme", newTheme);
  };

  const isActive = (path: string) => location.pathname === path;

  const userInitials = user?.email?.substring(0, 2).toUpperCase() || "VU";
  const vendorName = (user as any)?.vendorName || "Vendor Portal";

  return (
    <div className="flex h-screen" data-theme={theme}>
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-slate-900 dark:bg-slate-900 border-r border-slate-700/50 dark:border-slate-700/50 flex flex-col transition-all duration-300 z-20 shadow-2xl ${
          sidebarCollapsed ? "w-20" : "w-64"
        }`}
        style={{ background: "var(--bg-sidebar)" }}
      >
        {/* Logo */}
        <div
          className={`p-6 border-b border-slate-700/50 flex items-center gap-3 bg-slate-800/50 ${
            sidebarCollapsed ? "justify-center" : ""
          }`}
        >
          <img
            src="/logo.svg"
            alt="Metatalio Logo"
            className="w-10 h-10 flex-shrink-0"
          />
          {!sidebarCollapsed && (
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                {vendorName}
              </h1>
              <p className="text-xs text-slate-400">Vendor Portal</p>
            </div>
          )}
        </div>

        {/* Toggle Button Below Logo */}
        <div className="px-3 pt-2 pb-3">
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center gap-2 w-full py-2 rounded-lg transition-all bg-transparent hover:bg-slate-800 text-slate-400 hover:text-orange-400"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={
                  sidebarCollapsed
                    ? "M4 6h16M4 12h16M4 18h16"
                    : "M15 19l-7-7 7-7"
                }
              />
            </svg>
            {!sidebarCollapsed && (
              <span className="text-xs font-medium uppercase tracking-wide">
                Collapse
              </span>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto custom-scrollbar">
          <div className="space-y-1">
            <Link
              to="/vendor/dashboard"
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                isActive("/vendor/dashboard")
                  ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 font-medium border border-orange-500/30 shadow-lg shadow-orange-500/10"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              } ${sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-3"}`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              {!sidebarCollapsed && <span>Dashboard</span>}
            </Link>

            <Link
              to="/vendor/requisitions"
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                isActive("/vendor/requisitions")
                  ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 font-medium border border-orange-500/30 shadow-lg shadow-orange-500/10"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              } ${sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-3"}`}
            >
              <svg
                className="w-5 h-5 text-blue-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" />
              </svg>
              {!sidebarCollapsed && <span>Open Requisitions</span>}
            </Link>

            <Link
              to="/vendor/candidates"
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                isActive("/vendor/candidates")
                  ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 font-medium border border-orange-500/30 shadow-lg shadow-orange-500/10"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              } ${sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-3"}`}
            >
              <svg
                className="w-5 h-5 text-green-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
              </svg>
              {!sidebarCollapsed && <span>My Candidates</span>}
            </Link>

            <Link
              to="/vendor/submissions"
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                isActive("/vendor/submissions")
                  ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 font-medium border border-orange-500/30 shadow-lg shadow-orange-500/10"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              } ${sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-3"}`}
            >
              <svg
                className="w-5 h-5 text-purple-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
              {!sidebarCollapsed && <span>Submissions</span>}
            </Link>

            <Link
              to="/vendor/settings/profile"
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                location.pathname.startsWith("/vendor/settings")
                  ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 font-medium border border-orange-500/30 shadow-lg shadow-orange-500/10"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              } ${sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-3"}`}
            >
              <svg
                className="w-5 h-5 text-cyan-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
              </svg>
              {!sidebarCollapsed && <span>Settings</span>}
            </Link>
          </div>
        </nav>

        {/* Footer */}
        <div className="mt-auto">
          <div className="p-4 border-t border-slate-700">
            <Link
              to="/help"
              className={`flex items-center gap-2 rounded-lg text-orange-400 hover:bg-white/5 transition-all duration-200 ${
                sidebarCollapsed ? "justify-center px-1 py-2" : "px-4 py-2"
              }`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
              </svg>
              {!sidebarCollapsed && <span>Help</span>}
            </Link>
          </div>
        </div>
      </aside>

      {/* Top Header Bar */}
      <header
        className={`fixed top-0 right-0 h-16 border-b border-slate-700/50 flex items-center justify-between px-6 z-10 transition-all duration-300 shadow-xl`}
        style={{
          left: sidebarCollapsed ? "80px" : "256px",
          background: "var(--bg-header)",
          borderColor: "var(--border-primary)",
        }}
      >
        <div className="w-10"></div>

        {/* Center: Search Bar */}
        <div className="flex-1 max-w-xl mx-auto">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search candidates, jobs..."
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
            />
          </div>
        </div>

        {/* Right: Theme Toggle, Notification & User */}
        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-slate-900 hover:bg-slate-700 transition-colors border border-slate-600"
          >
            {theme === "dark" ? (
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-slate-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>

          {/* Notification Bell */}
          <button className="relative p-2 rounded-lg bg-slate-900 hover:bg-slate-700 transition-colors border border-slate-600">
            <svg
              className="w-6 h-6 text-slate-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </button>

          {/* User Profile */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center text-white font-semibold text-sm">
                {userInitials}
              </div>
            </button>

            {showUserDropdown && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg bg-slate-800 border border-slate-600 shadow-2xl overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-slate-600 bg-slate-700/50">
                  <div className="text-sm font-semibold text-white">
                    {user?.email?.split("@")[0] || "Vendor User"}
                  </div>
                  <div className="text-xs text-slate-400">
                    {user?.email || ""}
                  </div>
                  <div className="text-xs text-orange-400 mt-1">
                    {(user as any)?.vendorRole || "Vendor User"}
                  </div>
                </div>
                <a
                  href="#"
                  className="flex items-center gap-3 px-4 py-3 text-slate-200 hover:bg-slate-700 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span>Profile</span>
                </a>
                <a
                  href="#"
                  className="flex items-center gap-3 px-4 py-3 text-slate-200 hover:bg-slate-700 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Support</span>
                </a>
                <div className="border-t border-slate-600"></div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-slate-700 transition-colors text-left"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className={`flex-1 p-8 pt-24 transition-all duration-300 min-h-screen overflow-auto`}
        style={{
          marginLeft: sidebarCollapsed ? "80px" : "256px",
          width: `calc(100vw - ${sidebarCollapsed ? "80px" : "256px"})`,
          background:
            "linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)",
          color: "var(--text-primary)",
        }}
      >
        {children}
      </main>
    </div>
  );
};
